/**
 * The rail below the `lg` breakpoint: icons only — ADR 9004's amendment, audit v4 G-15, drawn as
 * the designer's brief part 1 §3 draws it (Package C4).
 *
 * `tokens/layout.json` has recorded this since the redesign ("below the `lg` breakpoint the
 * rail collapses to `sidebar-width-collapsed` and shows icons only"). The design constraints
 * that decided its shape:
 *
 * - **The same statements as the wide rail, in a narrower shape.** A not-yet-offered screen is
 *   still visible and inert; brand red still marks only where the planner is; the mark is still
 *   the way home. Nothing here reads a chapter list of its own — it is the wide rail's layout.
 * - **56px, icons in 40px squares.** The app holding the page is the square on `rail-active`,
 *   its icon white, with the red bar at the rail's edge.
 * - **An app opens a popover listing its entries** (ADR 9023: one icon per app), on hover or on
 *   focus: white, a 1px ink border, the overlay shadow, 232px wide, 6px off the rail and
 *   aligned to the icon. The app's name in Antonio 16 over a 2px rule, then its entries at
 *   13px, the current one on `menu-active` with the red bar at its start. Opened by hover or by
 *   focus it leaves focus where it is, so the arrow keys still walk the icons; Enter, Space or
 *   the inward arrow move focus into it; Escape closes it and focus returns to the icon (R-10).
 * - **Every control keeps a name.** An icon button's accessible name is the app's name; the
 *   popover's title says it on screen, and the icons with no popover carry a `title` (R-10).
 * - **Navigation first** (audit v5b R-09): the theme is ONE control opening its three choices,
 *   so three of the rail's icons are no longer about something other than going somewhere.
 *   Every control, navigation or not, is the same 40px square (R-04 as brief §3 restates it).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as RadixPopover from '@radix-ui/react-popover';
import { LayoutGrid, ListChecks, LogOut, Monitor, Moon, Search, Sun, UserRound } from 'lucide-react';

import { MY_WORK_PATH } from '../routes/my-work/my-work';
import { WorkBadge } from '../routes/my-work/WorkBadge';

import { BataWordmark } from './BataWordmark';
import { useFeedback } from '../feedback/FeedbackProvider';
import { AccountEntries } from './AccountEntries';
import { IdentityMark } from './RailGlyphs';
import { RAIL_GROUP_ICONS } from './rail-icons';
import { moveRailFocus, RAIL_ITEM } from './rail-groups';
import {
  FUTURE_GROUP, isFutureHere, type ActiveRailLink, type RailGroupId, type RailLayout, type RailLink,
} from './rail-layout';
import { THEME_CHOICES, type ThemeChoice } from '../../lib/theme';
import { SUPPORTED_LOCALES } from '../../lib/format';
import { i18next } from '../../i18n';
import { FOCUS_RING } from '../../primitives/focus';
import { DensityControl } from '../../planning/density-control';

const THEME_ICON = { light: Sun, dark: Moon, system: Monitor } as const;

/** A control of this rail: a 40px square — brief part 1 §3. */
const SQUARE = 'flex size-[40px] shrink-0 items-center justify-center';

export interface CollapsedRailProps {
  /** The wide rail's layout (ADR 9023), so both rails draw one list. */
  readonly layout: RailLayout;
  readonly active: ActiveRailLink | undefined;
  /** The chapter the path is in, for a future chapter reached by deep link. */
  readonly activeSlug: string | undefined;
  readonly onOpenLink: (link: RailLink) => void;
  readonly currentPath: string;
  readonly homePath: string;
  readonly onNavigate: (to: string) => void;
  readonly onOpenPalette: () => void;
  readonly themeChoice: ThemeChoice;
  readonly onThemeChoice: (choice: ThemeChoice) => void;
  readonly identity: string | undefined;
  readonly onLogout: () => void;
  readonly version: string;
  readonly commit: string;
  /** The rail's focus ring — the wide rail's constant, so both rails draw one ring. */
  readonly focus: string;
}

export function CollapsedRail({
  layout, active, activeSlug, onOpenLink, currentPath, homePath, onNavigate, onOpenPalette,
  themeChoice, onThemeChoice, identity, onLogout, version, commit, focus,
}: CollapsedRailProps): React.JSX.Element {
  const { t } = useTranslation();
  const feedback = useFeedback();
  // One app's entries at a time.
  const [openGroup, setOpenGroup] = useState<RailGroupId | null>(null);
  /** Opens `id`'s popover, or closes it — and only it: a late close from the app the pointer or
   *  the focus just left never closes the one it moved to. */
  const openChange = (id: RailGroupId) => (open: boolean): void => {
    setOpenGroup((was) => (open ? id : was === id ? null : was));
  };
  const futureHere = active === undefined && layout.future.some((item) => isFutureHere(item, currentPath, activeSlug));
  const iconButton = `text-rail-fg-secondary hover:text-rail-fg hover:bg-rail-hover ${SQUARE} ${focus}`;
  const navTarget = (current: boolean): string => [
    'relative', SQUARE,
    current ? 'bg-rail-active text-rail-fg' : 'text-rail-fg-entry hover:text-rail-fg hover:bg-rail-hover',
    focus,
  ].join(' ');
  const home = currentPath === homePath;
  const ThemeNow = THEME_ICON[themeChoice];

  return (
    <div className="flex h-full flex-col items-center gap-1 py-3">
      {/* The mark is the way home here too; at this width it is the shape, not the lettering. */}
      <button
        type="button"
        onClick={() => onNavigate(homePath)}
        aria-current={currentPath === homePath ? 'page' : undefined}
        className={`hover:bg-rail-hover ${SQUARE} ${focus}`}
      >
        <BataWordmark tone="rail" height={8} />
        <span className="sr-only">{t('nav.home')}</span>
      </button>

      <button type="button" onClick={onOpenPalette} title={t('palette.open')} className={iconButton}>
        <Search size={16} aria-hidden />
        <span className="sr-only">{t('palette.open')}</span>
      </button>

      {/* Its own scroll container, as in the wide rail: on a short window the apps scroll and
          the theme, identity and sign-out below keep their place. */}
      <nav aria-label={t('nav.label')} onKeyDown={moveRailFocus} className="flex min-h-0 w-full grow flex-col items-center gap-1 overflow-y-auto overscroll-contain pt-2">
        {/* The Cockpit, as in the wide rail — audit v5 N-06: on `/` the rail marks it. */}
        <button
          type="button"
          onClick={() => onNavigate(homePath)}
          aria-current={home ? 'page' : undefined}
          title={t('nav.home')}
          className={navTarget(home)}
        >
          {home && <EdgeBar />}
          <LayoutGrid size={18} aria-hidden />
          <span className="sr-only">{t('nav.home')}</span>
        </button>
        {/* "My work", as in the wide rail (ADR 9022). */}
        <button
          type="button"
          onClick={() => onNavigate(MY_WORK_PATH)}
          aria-current={currentPath === MY_WORK_PATH ? 'page' : undefined}
          title={t('nav.myWork')}
          className={navTarget(currentPath === MY_WORK_PATH)}
        >
          {currentPath === MY_WORK_PATH && <EdgeBar />}
          <ListChecks size={18} aria-hidden />
          <span className="sr-only">{t('nav.myWork')}</span>
          {/* bata-ui#166 W3-4 (ADR 9101): the same count as the wide rail, compact. */}
          <span className="absolute end-0 top-0"><WorkBadge compact /></span>
        </button>
        {/*
          One icon per app, then one for "Future expansions" — ADR 9023, the wide rail's
          dropdowns in the same order. The icon of the app holding the page carries the one
          brand red; its popover lists the app's entries, the current one marked.
        */}
        <div className="border-rail-hairline mt-1 flex w-full flex-col items-center gap-1 border-t pt-2">
          {layout.apps.map((app) => (
            <GroupPopover
              key={app.id}
              id={app.id}
              name={t(app.labelKey)}
              current={active?.app === app.id}
              open={openGroup === app.id}
              onOpenChange={openChange(app.id)}
              navTarget={navTarget}
            >
              {app.links.flatMap((link) => {
                const inLink = link.key === active?.link.key;
                // Sub-entries (ADR 9023's amendment of 2026-09-30) are always listed here,
                // indented under their parent: the popover is opened on purpose, and it is
                // the only place this rail can show them.
                return [
                  // The app's common entries stand after a rule here too (brief part 2 §1).
                  ...(link.common === true
                    ? [<span key={`${link.key}:rule`} aria-hidden="true" data-rail-common-rule="" className="border-hairline mx-[14px] my-1 block border-t" />]
                    : []),
                  <PopoverEntry
                    key={link.key} link={link} here={inLink && active?.child === undefined}
                    nested={false} onOpenLink={onOpenLink}
                  />,
                  ...link.children.map((child) => (
                    <PopoverEntry
                      key={child.key} link={child} here={inLink && child.key === active?.child?.key}
                      nested onOpenLink={onOpenLink}
                    />
                  )),
                ];
              })}
            </GroupPopover>
          ))}
          {layout.future.length > 0 && (
            <GroupPopover
              id={FUTURE_GROUP}
              name={t('nav.futureExpansions')}
              current={futureHere}
              open={openGroup === FUTURE_GROUP}
              onOpenChange={openChange(FUTURE_GROUP)}
              navTarget={navTarget}
            >
              {/* Said in words as well as in the tooltip: a touch screen has no hover. */}
              <p className="text-caption text-ink-secondary px-[14px] pb-1 pt-2">{t('nav.notYetAvailable')}</p>
              {layout.future.map((item) => {
                const { chapter } = item;
                const key = item.kind === 'chapter' ? chapter.slug : item.entry.to;
                const here = futureHere && isFutureHere(item, currentPath, activeSlug);
                return (
                  // Inert (DEC-91): no icon, no hover, `aria-disabled`, the reason as the tooltip.
                  <button
                    key={key}
                    type="button"
                    {...RAIL_ITEM}
                    aria-disabled
                    aria-current={here ? 'page' : undefined}
                    title={t('nav.notYetAvailable')}
                    className={[
                      'text-rail-fg-future flex cursor-not-allowed items-center px-[14px] py-2 text-start text-[13px] leading-4',
                      FOCUS_RING,
                      here ? 'font-semibold' : '',
                    ].join(' ')}
                  >
                    {item.kind === 'chapter'
                      ? t(chapter.navLabelKey)
                      : t('nav.futureScreen', { chapter: t(chapter.navLabelKey), screen: t(item.entry.labelKey) })}
                  </button>
                );
              })}
            </GroupPopover>
          )}
        </div>
      </nav>

      {/*
        The theme, as ONE control — audit v5b R-09. Its icon is the current choice, and it
        opens the three: still three, because §9's "follow the system" is a choice of its own,
        but no longer three rows of the rail.
      */}
      <RadixPopover.Root>
        <RadixPopover.Trigger asChild>
          <button type="button" title={t('theme.label')} className={iconButton}>
            <ThemeNow size={14} aria-hidden />
            <span className="sr-only">{t('theme.label')}</span>
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            side="right"
            align="end"
            sideOffset={8}
            aria-label={t('theme.label')}
            className="bg-surface-raised border-popover-border shadow-popover z-popover flex max-h-[var(--radix-popover-content-available-height)] min-w-[12rem] flex-col overflow-y-auto rounded-control border py-2"
          >
            <fieldset className="flex flex-col">
              <legend className="text-label text-ink-secondary px-3 pb-1">{t('theme.label')}</legend>
              {THEME_CHOICES.map((choice) => {
                const ThemeIcon = THEME_ICON[choice];
                const selected = themeChoice === choice;
                return (
                  <button
                    key={choice}
                    type="button"
                    onClick={() => onThemeChoice(choice)}
                    aria-pressed={selected}
                    className={[
                      'text-body min-h-row-comfortable flex items-center gap-2 px-3 text-start',
                      FOCUS_RING,
                      selected ? 'text-ink-primary font-semibold' : 'text-ink-primary hover:bg-surface-sunken',
                    ].join(' ')}
                  >
                    <ThemeIcon size={14} aria-hidden />
                    {t(`theme.${choice}`)}
                  </button>
                );
              })}
            </fieldset>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>

      {/*
        The identity menu, as in the wide rail (G-19): who is signed in, the LANGUAGE and the
        version. The language must be reachable in every state of the chrome, or a planner
        can be left in one they do not read; and G7.5 wants the version DISPLAYED, which 56px
        cannot do inline — so both live one press away, in words.
      */}
      <RadixPopover.Root>
        <RadixPopover.Trigger asChild>
          <button type="button" title={identity ?? t('locale.label')} translate={identity !== undefined ? 'no' : undefined} className={iconButton}>
            {identity !== undefined ? <IdentityMark name={identity} /> : <UserRound size={14} aria-hidden />}
            <span className="sr-only">{identity ?? t('locale.label')}</span>
          </button>
        </RadixPopover.Trigger>
        <RadixPopover.Portal>
          <RadixPopover.Content
            side="right"
            align="end"
            sideOffset={8}
            // «Report a problem» closes this menu to open a non-modal drawer: the focus Radix gives the
            // icon back would land outside the drawer and close it (bata-platform#686).
            {...(feedback === null ? {} : { onCloseAutoFocus: feedback.keepFocusInForm })}
            className="bg-surface-raised border-popover-border shadow-popover z-popover flex max-h-[var(--radix-popover-content-available-height)] min-w-[14rem] flex-col gap-2 overflow-y-auto rounded-control border p-3"
          >
            {identity !== undefined && <p translate="no" className="text-label text-ink-primary">{identity}</p>}
            <label className="text-label text-ink-secondary" htmlFor="locale-select-narrow">{t('locale.label')}</label>
            <select
              id="locale-select-narrow"
              value={i18next.language}
              onChange={(e) => { void i18next.changeLanguage(e.target.value); }}
              className={`bg-surface-raised border-hairline text-body text-ink-primary h-8 rounded-control border px-1 ${FOCUS_RING}`}
            >
              {SUPPORTED_LOCALES.map((locale) => (
                <option key={locale} value={locale}>{t(`locale.${locale}`)}</option>
              ))}
            </select>
            <DensityControl />
            {/* Only with someone signed in, as sign-out below. */}
            {identity !== undefined && <AccountEntries onNavigate={onNavigate} />}
            <p className="text-caption text-ink-secondary font-numeric tabular-nums">
              {t('app.versionFooter', { version, commit })}
            </p>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>

      {identity !== undefined && (
        <button type="button" onClick={onLogout} title={t('nav.logout')} className={iconButton}>
          <LogOut size={14} aria-hidden />
          <span className="sr-only">{t('nav.logout')}</span>
        </button>
      )}
    </div>
  );
}

/** The red bar at the rail's edge, beside a 40px square: 8px out, 8px in from top and bottom. */
function EdgeBar(): React.JSX.Element {
  // §5.2 rule 1, the active navigation indicator.
  return <span aria-hidden className="bg-brand-red absolute inset-y-2 start-[-8px] w-[3px]" />;
}

/** One entry in an app's popover; a sub-entry (`nested`) sits one step in from its parent. */
function PopoverEntry({ link, here, nested, onOpenLink }: {
  readonly link: RailLink;
  readonly here: boolean;
  readonly nested: boolean;
  readonly onOpenLink: (link: RailLink) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <RadixPopover.Close asChild>
      <button
        type="button"
        {...RAIL_ITEM}
        onClick={() => onOpenLink(link)}
        aria-current={here ? 'page' : undefined}
        className={[
          'text-ink-primary relative flex items-center py-2 pe-[14px] text-start text-[13px] leading-4',
          nested ? 'ps-8' : 'ps-[14px]',
          FOCUS_RING,
          here ? 'bg-menu-active text-ink-primary font-bold' : 'hover:bg-surface-sunken',
        ].join(' ')}
      >
        {/* The designer's `inset 3px 0 0` bar, drawn as an element so it follows the reading
            direction (a box-shadow offset is physical). */}
        {here && <span aria-hidden className="bg-brand-red absolute inset-y-0 start-0 w-[3px]" />}
        {t(link.entry.labelKey)}
      </button>
    </RadixPopover.Close>
  );
}

/** How long the pointer may take to cross the 6px from an icon to its popover. */
const HOVER_CLOSE_MS = 150;

/**
 * One app's icon and its popover — brief part 1 §3, audit v5b R-10.
 *
 * Hover or focus opens it and leaves focus where it is, so a keyboard walking the icons with
 * the arrows sees each app's entries without being pulled into them. A press (Enter, Space, a
 * click) or the inward arrow moves focus into it, onto the current entry if it lists one.
 * Escape closes it (Radix) and focus returns to the icon without opening it again. The pointer
 * leaving closes it after `HOVER_CLOSE_MS`, unless it reached the popover or focus is inside.
 */
function GroupPopover({ id, name, current, open, onOpenChange, navTarget, children }: {
  readonly id: RailGroupId;
  readonly name: string;
  readonly current: boolean;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly navTarget: (current: boolean) => string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const Icon = RAIL_GROUP_ICONS[id];
  const trigger = useRef<HTMLButtonElement>(null);
  const content = useRef<HTMLDivElement>(null);
  /** Move focus into the popover once it mounts: it was opened by a press, not by hover or focus. */
  const focusOnOpen = useRef(false);
  /** Focus is going back to the icon because the popover closed: do not reopen on that focus. */
  const returning = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancelClose = (): void => { clearTimeout(closeTimer.current); };
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const focusIsHere = (): boolean => {
    const focused = document.activeElement;
    return focused !== null && (focused === trigger.current || content.current?.contains(focused) === true);
  };
  const scheduleClose = (): void => {
    cancelClose();
    closeTimer.current = setTimeout(() => { if (!focusIsHere()) onOpenChange(false); }, HOVER_CLOSE_MS);
  };
  const focusList = (): void => {
    const list = content.current;
    (list?.querySelector<HTMLElement>('[data-rail-item][aria-current="page"]')
      ?? list?.querySelector<HTMLElement>('[data-rail-item]'))?.focus();
  };
  const press = (): void => {
    cancelClose();
    if (open && content.current !== null) focusList();
    else { focusOnOpen.current = true; onOpenChange(true); }
  };
  const changeOpen = (next: boolean): void => {
    // Closing with focus inside (Escape, an entry chosen): focus goes back to the icon.
    if (!next) returning.current = content.current?.contains(document.activeElement) === true;
    onOpenChange(next);
  };

  return (
    <RadixPopover.Root open={open} onOpenChange={changeOpen}>
      <RadixPopover.Trigger asChild>
        <button
          ref={trigger}
          type="button"
          {...RAIL_ITEM}
          aria-current={current ? 'page' : undefined}
          className={navTarget(current)}
          // `preventDefault` keeps Radix from toggling: a press opens, and never closes a
          // popover that hover or focus opened a moment before.
          onClick={(event) => { event.preventDefault(); press(); }}
          onKeyDown={(event) => {
            const inward = getComputedStyle(event.currentTarget).direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
            if (event.key === inward) { event.preventDefault(); press(); }
          }}
          onPointerEnter={() => { cancelClose(); onOpenChange(true); }}
          onPointerLeave={scheduleClose}
          onFocus={() => {
            if (returning.current) { returning.current = false; return; }
            onOpenChange(true);
          }}
        >
          {/* The one brand red in the rail, as in the wide rail: where the planner is. */}
          {current && <EdgeBar />}
          <Icon size={18} aria-hidden />
          <span className="sr-only">{name}</span>
        </button>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          ref={content}
          side="right"
          align="start"
          // 8px from the icon to the rail's edge, then the brief's 6px.
          sideOffset={14}
          aria-label={name}
          onKeyDown={moveRailFocus}
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (focusOnOpen.current) focusList();
            focusOnOpen.current = false;
          }}
          onCloseAutoFocus={(event) => {
            // Radix would focus the icon on EVERY close — a hover-close too, taking focus from
            // wherever the planner is. Only a close from inside hands it back.
            event.preventDefault();
            if (returning.current) trigger.current?.focus();
          }}
          className="bg-surface-raised border-popover-border shadow-popover z-popover flex max-h-[var(--radix-popover-content-available-height)] w-[232px] flex-col overflow-y-auto border"
        >
          <p className="font-display text-ink-primary border-ink-primary border-b-2 px-[14px] py-[10px] text-[16px] font-bold uppercase leading-5">
            {name}
          </p>
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
